import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDateHeure } from "@/components/scans/common";
import { api, errMsg } from "@/lib/api";
import { colisSubnav } from "@/lib/subnavs";
import { useAuth } from "@/context/AuthContext";
import { peut } from "@/lib/permissions";

export function StatutListe({ l }) {
  const ouverte = l.statut !== "cloturee";
  return (
    <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${ouverte ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30" : "bg-secondary text-muted-foreground border-border"}`}>
      {ouverte ? "En cours" : "Clôturée"}
    </span>
  );
}

/** Création d'une liste (nom facultatif) puis ouverture, caméra prête. */
export function CreerListeDialog({ open, onClose }) {
  const navigate = useNavigate();
  const [nom, setNom] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setNom(""); setBusy(false); } }, [open]);
  const creer = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/listes-colis", { nom });
      onClose();
      navigate(`/colis/listes/${data.id}?scan=1`);
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="creer-liste-dialog">
        <form onSubmit={creer} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Créer une liste de colis</DialogTitle>
            <DialogDescription>Ensuite, scannez et/ou saisissez les colis : ils s'ajoutent à la liste au fur et à mesure.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="liste-nom">Nom de la liste</Label>
            <Input id="liste-nom" value={nom} onChange={(e) => setNom(e.target.value)} maxLength={120} autoFocus placeholder="ex. Retours tournée 203 (facultatif)" data-testid="liste-nom" />
            <p className="text-xs text-muted-foreground">Sans nom : « Liste du {new Date().toLocaleDateString("fr-FR")} … ».</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Annuler</Button>
            <Button type="submit" disabled={busy} data-testid="liste-creer">{busy && <Loader2 size={14} className="animate-spin" />} Créer et commencer</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ListesColisPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listes, setListes] = useState(null);
  const [creer, setCreer] = useState(false);

  useEffect(() => { api.get("/listes-colis").then(({ data }) => setListes(data)).catch((e) => toast.error(errMsg(e))); }, []);

  return (
    <AppShell title="Listes de colis" subnav={colisSubnav()}>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Listes de colis</h1>
          <p className="text-sm text-muted-foreground">Scannez ou saisissez une série de colis, consultez-la ici ou exportez-la en CSV.</p>
        </div>
        {peut(user, "listes_creer") && <Button onClick={() => setCreer(true)} data-testid="btn-creer-liste"><Plus size={15} /> Créer une liste</Button>}
      </div>

      {!listes ? <p className="text-sm text-muted-foreground">Chargement…</p> : listes.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground">
          <ClipboardList className="mx-auto mb-2 opacity-60" />
          Aucune liste. Cliquez sur « Créer une liste ».
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="listes">
          {listes.map((l) => (
            <button key={l.id} type="button" onClick={() => navigate(`/colis/listes/${l.id}`)}
              className="text-left rounded-xl border border-border bg-card p-4 hover:border-primary/60 transition-colors" data-testid={`liste-${l.id}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="font-semibold leading-tight">{l.nom}</p>
                <StatutListe l={l} />
              </div>
              <p className="mt-2 font-mono text-2xl font-bold">{l.nb} <span className="text-sm font-sans font-normal text-muted-foreground">colis</span></p>
              {Object.keys(l.par_type).length > 0 && (
                <p className="text-xs text-muted-foreground">{Object.entries(l.par_type).map(([k, v]) => `${k} : ${v}`).join(" · ")}</p>
              )}
              <p className="text-xs text-muted-foreground mt-2">Créée le {fmtDateHeure(l.created_at)} par {l.created_by?.nom || "—"}</p>
            </button>
          ))}
        </div>
      )}
      <CreerListeDialog open={creer} onClose={() => setCreer(false)} />
    </AppShell>
  );
}
